/// <amd-module name="Wit/WorkItems/WorkItemSelectors" />
import * as Reselect from "Reselect";
import { equals } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { createShallowCompareSelector } from "VSS/Redux/Utils/CreateShallowCompareSelector";
import { FieldStatusFlags, WorkItemAttachment } from "Wit/Clients/Internal/Contracts/SocialWorkItem";
import { CoreFieldRefNames } from "Wit/Common/CoreFieldRefNames";
import { FieldValueMap, FieldValueType, IWorkItemTypeAwareState } from "Wit/WorkItemTypes/Contracts";
import { getWorkItemTypeFields } from "Wit/WorkItemTypes/Selectors/WorkItemTypeSelectors";
import { LinkReferenceNames } from "./LinkConstants";
import { areLinksEquivalent } from "./Utilities/WorkItemRelationUtilities";
import { isWorkItemValid } from "./Utilities/WorkItemUtilities";
import { ArtifactUpdateType, IWorkItem, IWorkItemAwareState, IWorkItemChange, PartialWorkItemLinkedItem, WorkItemStatus } from "./WorkItemContracts";

export function getWorkItem(state: IWorkItemAwareState, workItemId: number): IWorkItem | undefined {
    return state.workItemState && state.workItemState.workItems && state.workItemState.workItems[workItemId];
}

export const getWorkItems = createShallowCompareSelector(
    (state: IWorkItemAwareState, ids: number[]) => state.workItemState.workItems,
    (state: IWorkItemAwareState, ids: number[]) => ids,
    (workItems, ids) => ids.map(wId => workItems[wId])
);

export function getAllWorkItemIds(state: IWorkItemAwareState): number[] | undefined {
    return state.workItemState && state.workItemState.workItems && Object.keys(state.workItemState.workItems).map(Number);
}

export function getFieldValue<T extends FieldValueType>(state: IWorkItemAwareState, workItemId: number, fieldRefName: string): T | undefined {
    const workItem = getWorkItem(state, workItemId);
    return getFieldValueFromWorkItem(workItem, fieldRefName);
}

export function getFieldValueWithoutPendingChanges<T extends FieldValueType>(
    state: IWorkItemAwareState,
    workItemId: number,
    fieldRefName: string
): T | undefined {
    const workItem = getWorkItem(state, workItemId);
    return getFieldValueFromWorkItem(workItem, fieldRefName, false);
}

export function getFieldValueFromWorkItem<T extends FieldValueType>(
    workItem: IWorkItem | undefined,
    fieldRefName: string,
    includePendingChanges = true
): T | undefined {
    if (!workItem) {
        return undefined;
    }

    let fieldValue: T | undefined = workItem.fields && workItem.fields[fieldRefName.toLocaleLowerCase()];

    if (includePendingChanges) {
        // Check for any pending field updates
        if (workItem.pendingChanges) {
            for (const pendingChange of workItem.pendingChanges.reverse()) {
                if (pendingChange.fieldChanges) {
                    for (const pendingFieldChange of pendingChange.fieldChanges) {
                        if (equals(pendingFieldChange.fieldRefName, fieldRefName, true)) {
                            fieldValue = pendingFieldChange.value as T;
                            return fieldValue;
                        }
                    }
                }
            }
        }
    }

    return fieldValue;
}

function _getWorkItemLinks(state: IWorkItemAwareState, workItemId: number): PartialWorkItemLinkedItem[] | undefined {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.links;
}

function _getPendingChanges(state: IWorkItemAwareState, workItemId: number): IWorkItemChange[] | undefined {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.pendingChanges;
}

export const getWorkItemLinks = Reselect.createSelector(
    _getWorkItemLinks,
    _getPendingChanges,
    (existingLinks: PartialWorkItemLinkedItem[] | undefined, pendingChanges: IWorkItemChange[] | undefined) => {
        if (!existingLinks && !pendingChanges) {
            return undefined;
        }

        if (existingLinks && !pendingChanges) {
            return existingLinks;
        }

        // Create a copy of the links
        const links = (existingLinks || []).slice(0);

        // Apply pending changes if there are any
        for (const pendingChange of pendingChanges || []) {
            if (pendingChange.linkChanges) {
                for (const linkChange of pendingChange.linkChanges) {
                    switch (linkChange.updateType) {
                        case ArtifactUpdateType.Added:
                            links.push(linkChange);
                            break;

                        case ArtifactUpdateType.Removed: {
                            const idx = links.findIndex(x => areLinksEquivalent(x, linkChange));
                            if (idx >= 0) {
                                links.splice(idx, 1);
                            }
                            break;
                        }

                        case ArtifactUpdateType.Updated:
                        case ArtifactUpdateType.Replaced: {
                            const idx = links.findIndex(x => areLinksEquivalent(x, linkChange));
                            if (idx >= 0) {
                                links[idx] = linkChange;
                            }
                            break;
                        }
                    }
                }
            }
        }

        return links;
    }
);

export function getWorkItemTitle(state: IWorkItemAwareState, workItemId: number): string | undefined {
    return getFieldValue<string>(state, workItemId, CoreFieldRefNames.Title);
}

export function getWorkItemAssignedTo(state: IWorkItemAwareState, workItemId: number): IdentityRef | undefined {
    return getFieldValue<IdentityRef>(state, workItemId, CoreFieldRefNames.AssignedTo);
}

export function getWorkItemProjectId(state: IWorkItemAwareState, workItemId: number): string | undefined {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.projectId;
}

export function getWorkItemProjectName(state: IWorkItemAwareState, workItemId: number): string | undefined {
    return getFieldValue<string>(state, workItemId, CoreFieldRefNames.TeamProject);
}

export function getWorkItemTypeName(state: IWorkItemAwareState, workItemId: number): string | undefined {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.workItemTypeName;
}

export function getWorkItemStatus(state: IWorkItemAwareState, workItemId: number): WorkItemStatus {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.status ? workItem.status : WorkItemStatus.NotLoaded;
}

export function getWorkItemError(state: IWorkItemAwareState, workItemId: number): string | undefined {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.error;
}

export function getAttachments(state: IWorkItemAwareState, workItemId: number): WorkItemAttachment[] | undefined {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.attachments;
}

export function getWorkItemFields(state: IWorkItemAwareState, workItemId: number): FieldValueMap | undefined {
    const workItem = getWorkItem(state, workItemId);
    return workItem && workItem.fields;
}

/** A helper selector to get the work item id of the parent link */
export const getParentWorkItemId = Reselect.createSelector(
    getWorkItemLinks,
    (links): number | undefined => {
        const parentLink = links && links.find(link => equals(link.referenceName, LinkReferenceNames.Parent, true));
        if (parentLink) {
            return parentLink.targetWorkItemId;
        }

        return undefined;
    }
);

function _getWorkItemTypeNames(state: IWorkItemAwareState): { [id: number]: string } {
    const workItemTypeNameMap: { [id: number]: string } = {};
    if (state && state.workItemState) {
        Object.keys(state.workItemState.workItems).forEach(id => {
            const name = getWorkItemTypeName(state, Number(id));
            if (name) {
                workItemTypeNameMap[Number(id)] = name;
            }
        });
    }
    return workItemTypeNameMap;
}

export function getRelatedWorkItemIdsSelector(linkReferenceName: string, workItemTypeName: string) {
    return createShallowCompareSelector(_getWorkItemTypeNames, getWorkItemLinks, (workItemTypeNames, links) => {
        const children = links && links.filter(link => equals(link.referenceName, linkReferenceName, true));
        if (children) {
            const ids: (number | undefined)[] = children.map(childLink => {
                return childLink.targetWorkItemId;
            });

            return ids.filter(id => {
                if (id !== undefined) {
                    const resolvedWorkItemTypeName = workItemTypeNames[id];
                    return resolvedWorkItemTypeName && equals(resolvedWorkItemTypeName, workItemTypeName, true);
                }

                return false;
            }) as number[];
        }

        return undefined;
    });
}

/** Used to see if a new work item has any field values to see if the newly created work item is dirty. */
export function canCreateWorkItem(state: IWorkItemAwareState & IWorkItemTypeAwareState, workItemId: number): boolean {
    const workitem = getWorkItem(state, workItemId);
    if (workitem && workitem.fields) {
        const projectId = getWorkItemProjectId(state, workItemId);
        const workItemTypeName = getWorkItemTypeName(state, workItemId);

        if (projectId && workItemTypeName) {
            const typeFields = getWorkItemTypeFields(state, projectId, workItemTypeName);
            return isWorkItemValid(workitem, typeFields);
        }
    }
    return false;
}

function _getFieldProperties(state: IWorkItemAwareState, workItemId: number) {
    return state.workItemState && state.workItemState.fieldProperties && state.workItemState.fieldProperties[workItemId];
}

export function getAllowedValues(state: IWorkItemAwareState, workItemId: number, fieldRefName: string) {
    const properties = _getFieldProperties(state, workItemId);
    return properties && properties.allowedValues && properties.allowedValues[fieldRefName.toLocaleLowerCase()];
}

export function getWorkItemFieldFlags(state: IWorkItemAwareState, workItemId: number, fieldRefName: string): FieldStatusFlags | undefined {
    const properties = _getFieldProperties(state, workItemId);
    const fieldRefNameKey = fieldRefName.toLocaleLowerCase();

    return properties && properties.fieldFlags && properties.fieldFlags[fieldRefNameKey];
}
